import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import ConfirmationModal from "../../components/ConfirmationModal";
import AiAssetModal from "../../components/AiAssetModal";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles
    }
}));

const AiAssets = () => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const fetchAssets = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ai-assets");
            setAssets(Array.isArray(data) ? data : []);
        } catch (err) {
            toastError(err);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    const handleDelete = async () => {
        try {
            await api.delete(`/ai-assets/${deletingId}`);
            toast.success("Asset removido");
            fetchAssets();
        } catch (err) {
            toastError(err);
        }
        setConfirmOpen(false);
        setDeletingId(null);
    };

    if (user.profile === "user") {
        return <ForbiddenPage />;
    }

    return (
        <MainContainer>
            <ConfirmationModal
                title="Excluir asset"
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDelete}
            >
                Deseja realmente excluir este asset?
            </ConfirmationModal>
            <AiAssetModal
                open={modalOpen}
                onClose={() => { setModalOpen(false); setSelectedAssetId(null); }}
                assetId={selectedAssetId}
                reload={fetchAssets}
            />
            <MainHeader>
                <Title>Biblioteca de mídias e links (IA)</Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => { setSelectedAssetId(null); setModalOpen(true); }}
                    >
                        Adicionar asset
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Rótulo</TableCell>
                            <TableCell>Nome (IA)</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Escopo</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && assets.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Nenhum asset cadastrado</TableCell>
                            </TableRow>
                        )}
                        {assets.map((asset) => (
                            <TableRow key={asset.id}>
                                <TableCell>{asset.label}</TableCell>
                                <TableCell>{asset.name}</TableCell>
                                <TableCell>{asset.type}</TableCell>
                                <TableCell>
                                    {asset.promptId ? `Prompt #${asset.promptId}` : asset.queueId ? `Fila #${asset.queueId}` : "Empresa"}
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => { setSelectedAssetId(asset.id); setModalOpen(true); }}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => { setDeletingId(asset.id); setConfirmOpen(true); }}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default AiAssets;
